import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Form, Message } from "@arco-design/web-react";
import axios from "axios";
import classNames from "classnames";

import { endpoints } from "../../../api/endpoints";
import { RoleDropdown } from "../../../components/RoleDropdown/RoleDropdown";
import { Role } from "../../../models/Person";
import { UserForm } from "../UserForm/UserForm";
import { RootState } from "../../../store/store";
import { currentThemeName } from "../../../theme";
import { useEventIntakeEnabled } from "../../../hooks/useEventIntakeEnabled";

import './CreateUser.css';

export function CreateUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const currentUser = useSelector((state: RootState) => state.user.user);
  const eventIntakeEnabled = useEventIntakeEnabled();

  const [role, setRole] = useState<Role>('mentor');
  const showAdminOption = Boolean(currentUser?.isSuperAdmin);

  const onSubmit = () => {
    if (form) {
      const fields = form.getFields();
      const formData = new FormData();

      formData.append("isMentor", Boolean(role === "mentor").toString());
      formData.append("isAdmin", Boolean(role === "admin").toString());
      formData.append("isGuest", Boolean(role === "guest").toString());

      Object.keys(fields).forEach((key) => {
        if (Array.isArray(fields[key])) {
          fields[key].forEach((value: string) => {
            formData.append(key, value);
          });
        } else {
          formData.append(key, fields[key]);
        }
      });

      form.validate().then(() => {
        axios
          .post(endpoints.admin.createUser, formData)
          .then((response) => {
            if (response.data?.email_sent === false) {
              Message.warning({
                content: t('admin.create_user.email_warning_message'),
                closable: true,
                duration: 8000,
              });
            } else {
              Message.success({
                content: t('admin.create_user.success_message'),
                closable: true,
                duration: 5000,
              });
            }
            form.resetFields();
          })
          .catch((error) => {
            console.error(error);
            if (error.response?.status === 403) {
              const message = error.response?.data;
              if (message === 'event intake disabled') {
                Message.error(t('notification_messages.event_intake_disabled'));
              } else {
                Message.error(t('admin.create_user.super_admin_required'));
              }
              return;
            }
            if (error.response && error.response.status === 409) {
              Message.error(t('notification_messages.email_exists_error'));
            } else if (typeof error === "object" && error !== null) {
              const err = error as Record<string, any>;

              // Check for backend error structure
              if (
                err.error?.code === 400 &&
                err.error.message === "EMAIL_EXISTS"
              ) {
                Message.error(t('notification_messages.email_exists_error'));
              } else if (err.error?.code === 500) {
                Message.error(t('notification_messages.server_error'));
              } else if (err.code) {
                // Firebase error handling
                switch (err.code) {
                  case "auth/email-already-in-use":
                    Message.error(t('notification_messages.sign_up_error_duplicate_email'));
                    break;
                  case "auth/invalid-email":
                    Message.error(t('notification_messages.invalid_email_error'));
                    break;
                  default:
                    Message.error(t('notification_messages.sign_up_error'));
                }
              } else {
                Message.error(t('notification_messages.unexpected_error'));
              }
            } else {
              Message.error(t('notification_messages.unexpected_error'));
            }
          })
          .finally(() => {});
      });
    }
  };

  return (
    <div className={classNames("create-user", {
      "create-user--black-theme": currentThemeName === "black"
    })}>
      <h1 className="create-user__title">{t('admin.create_user.title')}</h1>
      <RoleDropdown
        onRoleChange={setRole}
        role={role}
        showAdminOption={showAdminOption}
        showGuestOption={eventIntakeEnabled}
      />
      <div className="create-user__form-wrapper">
        <UserForm
          role={role}
          form={form}
          onSubmit={onSubmit}
          onCancel={() => {
            navigate(-1);
          }}
        />
      </div>
    </div>
  );
}
